import { reactive } from 'vue'
import { convertFileSrc } from '@tauri-apps/api/core'
import { invoke } from '~/lib/tauriInvoke'
import { isTauriRuntime } from '~/lib/platform/runtime'
import * as Sentry from '@sentry/nuxt'

export type MediaCacheCategory = 'avatars' | 'banners' | 'attachments'

export interface CategoryCacheStats {
  category: MediaCacheCategory
  size_bytes: number
  file_count: number
}

export interface MediaCacheStats {
  total_size_bytes: number
  categories: CategoryCacheStats[]
}

export const MEDIA_CACHE_CATEGORY_LABELS: Record<MediaCacheCategory, string> = {
  attachments: 'Вложения',
  avatars: 'Аватары',
  banners: 'Баннеры',
}

// Fixed slots 1-3 (blue/orange/aqua) of the app's validated categorical palette — this ordering
// clears every CVD/contrast gate in both light and dark mode for up to three series (see the
// dataviz skill's `references/palette.md`), so the mapping stays put rather than being derived
// from category size (which would repaint segments on every cache change).
export const MEDIA_CACHE_CATEGORY_COLORS: Record<MediaCacheCategory, { light: string; dark: string }> = {
  attachments: {light: '#3b82f6', dark: '#60a5fa'},
  avatars: {light: '#f97316', dark: '#fb923c'},
  banners: {light: '#10b981', dark: '#34d399'},
}
export function formatCacheSize(bytes: number): string {
  if (bytes <= 0) return '0 Б'
  const units = ['Б', 'КБ', 'МБ', 'ГБ']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exponent
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`
}

// Keyed by `${category}:${remoteUrl}`. Populated at most once per URL for the lifetime of the
// app process — reactive so template expressions that call `resolveMediaUrl` re-render once the
// download lands, and deduped via `pending` so the same URL asked for from multiple components at
// once only downloads once.
const resolvedUrls = reactive<Record<string, string>>({})
const pending = new Map<string, Promise<string>>()

function cacheKey(category: MediaCacheCategory, remoteUrl: string): string {
  return `${category}:${remoteUrl}`
}

async function fetchCachedMedia(category: MediaCacheCategory, remoteUrl: string): Promise<string> {
  // There's no on-disk cache (or `asset://` protocol) outside Tauri — skip straight to the raw
  // URL instead of round-tripping through an invoke that can only ever reject.
  if (!isTauriRuntime()) return remoteUrl

  try {
    const localPath = await invoke<string>('get_cached_media', { url: remoteUrl, category })
    return convertFileSrc(localPath)
  } catch (error) {
    console.error(`Failed to cache media (${category}) for ${remoteUrl}`, error)
    Sentry.captureException(error, { level: 'warning', extra: { action: 'fetch_cached_media', category, remoteUrl } })
    // Fall back to the raw CDN URL so the image still has a chance to render even if the
    // on-disk cache is unavailable (e.g. offline with nothing cached yet).
    return remoteUrl
  }
}

/** Kicks off (or reuses an in-flight) resolution of `remoteUrl` into a cached `asset://` URL. */
function ensureResolving(category: MediaCacheCategory, remoteUrl: string): Promise<string> {
  const key = cacheKey(category, remoteUrl)

  const inFlight = pending.get(key)
  if (inFlight) return inFlight

  const request = fetchCachedMedia(category, remoteUrl).then((url) => {
    resolvedUrls[key] = url
    pending.delete(key)
    return url
  })
  pending.set(key, request)
  return request
}

/**
 * Returns a locally-cached (`asset://`) URL for `remoteUrl`, kicking off a download into the
 * on-disk media cache the first time it's asked for. Safe to call repeatedly from a template
 * expression — reactive, and a no-op once resolved or while a request for that URL is already in
 * flight. Returns the raw remote URL synchronously until the cached version is ready.
 */
export function resolveMediaUrl(remoteUrl: string | undefined | null, category: MediaCacheCategory): string {
  if (!remoteUrl) return ''

  const key = cacheKey(category, remoteUrl)
  if (!(key in resolvedUrls)) void ensureResolving(category, remoteUrl)

  return resolvedUrls[key] ?? remoteUrl
}

export async function getMediaCacheStats(): Promise<MediaCacheStats> {
  return invoke<MediaCacheStats>('get_media_cache_stats')
}

/** Clears a single category, or the entire media cache when `category` is omitted. */
export async function clearMediaCache(category?: MediaCacheCategory): Promise<void> {
  await invoke('clear_media_cache', { category: category ?? null })

  const prefix = category ? `${category}:` : ''
  for (const key of Object.keys(resolvedUrls)) {
    if (!category || key.startsWith(prefix)) delete resolvedUrls[key]
  }
}
